import type { IntakeStep } from "../types";
import { STATIC_CONTRACT_CATALOG } from "./contracts";
import { targetIdForField, targetIdForFieldOption, targetIdForGuard, targetIdForRepeater, targetIdForRepeaterField, targetIdForRepeaterFieldOption, targetIdForStep, targetIdForUpload } from "./ids";
import type { FormCatalogItem, FormConfigVersion } from "./types";

export function createFormCatalogFromSteps(steps: IntakeStep[]): FormCatalogItem[] {
  const catalog: FormCatalogItem[] = [];
  for (const step of steps) {
    const stepId = targetIdForStep(step);
    catalog.push({ id: stepId, kind: "step", label: step.title, description: step.description, defaultVisible: !step.defaultHidden });
    for (const field of step.fields ?? []) {
      const fieldId = targetIdForField(field);
      catalog.push({
        id: fieldId,
        kind: "field",
        parentId: stepId,
        fieldPath: field.path,
        label: field.label,
        description: field.helperText,
        placeholder: field.placeholder,
        checkboxText: field.checkboxText,
        defaultValue: field.defaultValue,
        defaultVisible: !field.defaultHidden,
        defaultRequired: Boolean(field.required),
      });
      for (const option of field.options ?? []) {
        catalog.push({
          id: targetIdForFieldOption(field, option),
          kind: "option",
          parentId: fieldId,
          label: option.label,
          optionValue: option.value,
          description: option.description,
          defaultVisible: true,
        });
      }
    }
    for (const guard of step.guards ?? []) {
      catalog.push({
        id: targetIdForGuard(guard),
        kind: "guard",
        parentId: stepId,
        label: guard.label,
        description: guard.description,
        defaultVisible: !guard.defaultHidden,
      });
    }
    for (const repeater of step.repeaters ?? []) {
      const repeaterId = targetIdForRepeater(repeater);
      catalog.push({ id: repeaterId, kind: "repeater", parentId: stepId, label: repeater.label, description: repeater.description, defaultVisible: true });
      for (const field of repeater.fields) {
        const fieldId = targetIdForRepeaterField(repeater, field);
        catalog.push({
          id: fieldId,
          kind: "repeaterField",
          parentId: repeaterId,
          fieldPath: `${repeater.path}.${field.path}`,
          label: field.label,
          description: field.helperText,
          placeholder: field.placeholder,
          checkboxText: field.checkboxText,
          defaultValue: field.defaultValue,
          defaultVisible: !field.defaultHidden,
          defaultRequired: Boolean(field.required),
        });
        for (const option of field.options ?? []) {
          catalog.push({
            id: targetIdForRepeaterFieldOption(repeater, field, option),
            kind: "option",
            parentId: fieldId,
            label: option.label,
            optionValue: option.value,
            description: option.description,
            defaultVisible: true,
          });
        }
      }
    }
    for (const upload of step.fileUploads ?? []) {
      catalog.push({
        id: targetIdForUpload(upload),
        kind: "upload",
        parentId: stepId,
        label: upload.label,
        description: upload.description,
        defaultVisible: !upload.defaultHidden,
        defaultRequired: Boolean(upload.required),
      });
    }
  }
  return mergeFormCatalog(STATIC_CONTRACT_CATALOG, catalog);
}

export function mergeFormCatalog(...catalogs: FormCatalogItem[][]): FormCatalogItem[] {
  const byId = new Map<string, FormCatalogItem>();
  for (const catalog of catalogs) {
    for (const item of catalog) {
      byId.set(item.id, { ...byId.get(item.id), ...item });
    }
  }
  return Array.from(byId.values());
}

export function reconcileCatalogWithRuntime(stored: FormCatalogItem[], runtime: FormCatalogItem[]): FormCatalogItem[] {
  const runtimeIds = new Set(runtime.map((item) => item.id));
  const keptStored = stored.filter((item) => item.kind !== "option" || runtimeIds.has(item.id));
  const merged = mergeFormCatalog(keptStored, runtime);
  const rank = new Map(runtime.map((item, index) => [item.id, index]));
  return merged
    .map((item, index) => ({ item, order: rank.get(item.id) ?? runtime.length + index }))
    .sort((left, right) => left.order - right.order)
    .map(({ item }) => item);
}

export function withRuntimeCatalog(config: FormConfigVersion, steps: IntakeStep[]): FormConfigVersion {
  return { ...config, catalog: mergeFormCatalog(config.catalog, createFormCatalogFromSteps(steps)) };
}
